import {
  Button,
  CascaderField,
  Col,
  DatePickerField,
  FieldLabel,
  Flex,
  Form,
  MonetaryInputField,
  RemovableCard,
  Row,
  SelectField,
  SubmitButton,
  Text,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import {
  useAccountingTypeOptions,
  useNestedExpenditureBudgetPurposeOptions,
} from '@/features/master-table/hooks';
import { useProjectOptions } from '@/features/project/_hooks/project';
import {
  ExistenceOrAbsenceLabelModifier,
  FundBudgetClassification,
  FundBudgetClassificationModifier,
  FundBudgetTypeLabelModifier,
  KsAdditionalAttributesFormSchema,
  MeansOfDeliveryLabelModifier,
  fundBudgetType,
  meansOfDelivery,
} from '@/features/project/projects/_models/project';
import { isBefore2025 as isBefore2025Func } from '@/features/project/review-sheet/utils/project';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { booleanStringType } from '@/utils';
import { type fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import {
  type Control,
  type FieldErrors,
  type UseFormResetField,
  type UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof KsAdditionalAttributesFormSchema>;

type RelatedReviewSheetFieldProps = {
  fieldIndex: number;
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  resetField: UseFormResetField<FormValues>;
  setValue: UseFormSetValue<FormValues>;
};

const RelatedReviewSheetField: FC<RelatedReviewSheetFieldProps> = ({
  fieldIndex,
  control,
  errors,
  resetField,
  setValue,
}) => {
  const fieldKeys = {
    relatedReviewSheetYear: `compositionBackgrounds.${fieldIndex}.relatedReviewSheetYear`,
    relatedReviewSheetId: `compositionBackgrounds.${fieldIndex}.relatedReviewSheetId`,
    relatedReviewSheetName: `compositionBackgrounds.${fieldIndex}.relatedReviewSheetName`,
  } as const;

  const [relatedReviewSheetYear, relatedReviewSheetId] = useWatch({
    control,
    name: [fieldKeys.relatedReviewSheetYear, fieldKeys.relatedReviewSheetId],
  });

  const fiscalYear = relatedReviewSheetYear?.year().toString();

  const { t } = useTranslation();
  const { id: projectId } = useId();
  const projectOptions = useProjectOptions({
    params: {
      fiscalYear,
      sheetType: sheetTypeEnum.Values.RS,
      skipAdministrativeReformTeamEvaluation: false,
    },
    enabled: !!fiscalYear,
  });

  return (
    <Row gutter={[24, 0]}>
      <Col span={24} style={{ marginBottom: 8 }}>
        <FieldLabel text={t('related-review-sheet')} />
      </Col>

      <Col span={12}>
        <DatePickerField
          name={fieldKeys.relatedReviewSheetYear}
          label={t('created-year')}
          control={control}
          errors={errors}
          picker="year"
          onChange={(value) => {
            setValue(fieldKeys.relatedReviewSheetYear, value);

            resetField(fieldKeys.relatedReviewSheetId, {
              keepDirty: false,
              defaultValue: null,
            });
          }}
        />
      </Col>

      <Col span={12}>
        <SelectField
          name={fieldKeys.relatedReviewSheetId}
          label={t('project-name')}
          control={control}
          errors={errors}
          options={projectOptions.data?.filter(
            (option) => option.value !== projectId,
          )}
          showSearch
          disabled={!fiscalYear}
          onChange={(value) => {
            setValue(fieldKeys.relatedReviewSheetId, value ?? null);

            resetField(fieldKeys.relatedReviewSheetName, {
              keepDirty: false,
              defaultValue: null,
            });
          }}
        />
      </Col>

      <Col span={24}>
        <TextInputField
          name={fieldKeys.relatedReviewSheetName}
          label={t('optional-related-project-name')}
          control={control}
          errors={errors}
          disabled={!!relatedReviewSheetId}
        />
      </Col>
    </Row>
  );
};

export const emptyCompositionBackground = {
  fiscalYearOfBudgetaryProvision: null,
  fiscalYearOfCreation: null,
  meansOfDelivery: null,
  accountingId: '',
  budget: null,
  expenditureBudgetPurposeIds: [],
  expenditurePurposeName: null,
  amountOfStateExpenditure: null,
  haveAppliedLawOfAppropriatenessOfSubsidies: null,
  relatedReviewSheetYear: null,
  relatedReviewSheetId: null,
  relatedReviewSheetName: null,
};

type Props = {
  fundSheetType?: z.infer<typeof fundSheetTypeEnum>;
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const FundAdditionalAttributesForm: FC<Props> = ({
  fiscalYear,
  fundSheetType,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const accountOptions = useAccountingTypeOptions();
  const expenditureBudgetPurposeOptions =
    useNestedExpenditureBudgetPurposeOptions();

  const {
    handleSubmit,
    control,
    resetField,
    setValue,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(KsAdditionalAttributesFormSchema),
  });

  const {
    fields: compositionBackgroundsFields,
    append: appendCompositionBackgrounds,
    remove: removeCompositionBackgrounds,
  } = useFieldArray({
    control,
    name: 'compositionBackgrounds',
  });
  const {
    fields: nationalTreasuryReturnBackgroundsFields,
    append: appendNationalTreasuryReturnBackgrounds,
    remove: removeNationalTreasuryReturnBackgrounds,
  } = useFieldArray({
    control,
    name: 'nationalTreasuryReturnBackgrounds',
  });

  const isBefore2025 = useMemo(
    () => isBefore2025Func({ fiscalYear }),
    [fiscalYear],
  );

  const isKsLocalGovernmentAfter2025 = useMemo(
    () => !isBefore2025 && fundSheetType === 'local-government',
    [isBefore2025, fundSheetType],
  );

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Text
        variant="h5"
        text={t('composition-backgrounds')}
        style={{ display: 'block', paddingBottom: 16 }}
      />
      <Form.Item>
        <Flex vertical gap={8}>
          {compositionBackgroundsFields.map((item, index) => {
            return (
              <RemovableCard
                key={item.id}
                hideCloseButton={isKsLocalGovernmentAfter2025}
                onRemove={() => {
                  removeCompositionBackgrounds(index);
                }}
              >
                <Row gutter={[24, 0]}>
                  {!isKsLocalGovernmentAfter2025 && (
                    <Col span={12}>
                      <DatePickerField
                        name={`compositionBackgrounds.${index}.fiscalYearOfBudgetaryProvision`}
                        label={t('fiscal-year-of-budgetary-provision')}
                        control={control}
                        errors={errors}
                        picker="year"
                      />
                    </Col>
                  )}
                  <Col span={12}>
                    <DatePickerField
                      name={`compositionBackgrounds.${index}.fiscalYearOfCreation`}
                      label={
                        index > 0
                          ? t('added-fiscal-year')
                          : t('fiscal-year-of-creation')
                      }
                      control={control}
                      errors={errors}
                      picker="year"
                    />
                  </Col>
                  {initialValues?.fundSheetType === 'standard' && (
                    <>
                      <Col span={12}>
                        <SelectField
                          name={`compositionBackgrounds.${index}.meansOfDelivery`}
                          label={t('means-of-delivery')}
                          control={control}
                          errors={errors}
                          options={meansOfDelivery.options.map((value) => ({
                            value,
                            label: MeansOfDeliveryLabelModifier[value],
                          }))}
                        />
                      </Col>
                      <Col span={12}>
                        <SelectField
                          name={`compositionBackgrounds.${index}.accountingId`}
                          label={t('accounting-type')}
                          control={control}
                          errors={errors}
                          options={accountOptions.data?.map((item) => ({
                            value: item.value,
                            label: item.label,
                          }))}
                        />
                      </Col>
                      <Col span={12}>
                        <SelectField
                          name={`compositionBackgrounds.${index}.budget`}
                          label={t('fund-budget-type')}
                          control={control}
                          errors={errors}
                          options={fundBudgetType.options.map((value) => ({
                            value,
                            label: FundBudgetTypeLabelModifier[value],
                          }))}
                        />
                      </Col>
                      <Col span={12}>
                        <CascaderField
                          name={`compositionBackgrounds.${index}.expenditureBudgetPurposeIds`}
                          label={t('expenditure-budget-name')}
                          control={control}
                          errors={errors}
                          options={expenditureBudgetPurposeOptions.data}
                          multiple
                          showCheckedStrategy="SHOW_CHILD"
                        />
                      </Col>
                      <Col span={24}>
                        <TextAreaInputField
                          name={`compositionBackgrounds.${index}.expenditurePurposeName`}
                          label={t('expenditure-budget-name-for-old')}
                          control={control}
                          errors={errors}
                          rows={2}
                        />
                      </Col>
                      <Col span={12}>
                        <MonetaryInputField
                          name={`compositionBackgrounds.${index}.amountOfStateExpenditure`}
                          control={control}
                          label={t('amount-of-state-expenditure')}
                          errors={errors}
                          unit={unit}
                        />
                      </Col>
                    </>
                  )}
                  <Col span={12}>
                    <SelectField
                      name={`compositionBackgrounds.${index}.haveAppliedLawOfAppropriatenessOfSubsidies`}
                      label={t(
                        'have-applied-law-of-appropriateness-of-subsidies',
                      )}
                      control={control}
                      errors={errors}
                      options={booleanStringType.options.map((value) => ({
                        value,
                        label: ExistenceOrAbsenceLabelModifier[value],
                      }))}
                    />
                  </Col>

                  {initialValues?.fundSheetType === 'standard' && (
                    <Col span={24}>
                      <RelatedReviewSheetField
                        fieldIndex={index}
                        control={control}
                        errors={errors}
                        resetField={resetField}
                        setValue={setValue}
                      />
                    </Col>
                  )}
                </Row>
              </RemovableCard>
            );
          })}
          {!isKsLocalGovernmentAfter2025 && (
            <Button
              type="dashed"
              onClick={() => {
                appendCompositionBackgrounds(emptyCompositionBackground);
              }}
              block
              icon={<PlusOutlined />}
            >
              {t('add-composition-backgrounds')}
            </Button>
          )}
        </Flex>
      </Form.Item>

      {!isBefore2025 && initialValues?.fundSheetType === 'standard' && (
        <>
          <Text
            variant="h5"
            text={t('budget-measure-for-fund')}
            style={{ display: 'block', paddingBottom: 16 }}
          />
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <SelectField
                name="fundBudgetClassification"
                label={t('classification-of-the-fund')}
                hint={t('hint-classification-of-the-fund')}
                control={control}
                errors={errors}
                options={FundBudgetClassification.options.map((value) => ({
                  value,
                  label: FundBudgetClassificationModifier[value],
                }))}
              />
            </Col>
            <Col span={24}>
              <TextAreaInputField
                name="recentBudgetMeasureYear"
                label={t('latest-budget-year')}
                control={control}
                errors={errors}
              />
            </Col>
            <Col span={24}>
              <TextAreaInputField
                name="budgetReasonWithAchievementStatusAndReviewResult"
                label={t('budget-reason-and-outcome')}
                hint={t('hint-budget-reason-and-outcome')}
                control={control}
                errors={errors}
                rows={2}
              />
            </Col>
            <Col span={24}>
              <TextAreaInputField
                name="nextBudgetMeasureReviewYear"
                label={t('next-budget-review-year')}
                control={control}
                errors={errors}
              />
            </Col>
          </Row>
        </>
      )}

      {initialValues?.fundSheetType === 'standard' && (
        <>
          <Text
            variant="h5"
            text={t('national-treasury-return-backgrounds')}
            style={{ display: 'block', paddingBottom: 16 }}
          />
          <Form.Item>
            <Row gutter={[0, 8]}>
              {nationalTreasuryReturnBackgroundsFields.map((item, idx) => {
                return (
                  <Col key={item.id} span={24}>
                    <RemovableCard
                      onRemove={() => {
                        removeNationalTreasuryReturnBackgrounds(idx);
                      }}
                    >
                      <Row gutter={[24, 0]}>
                        <Col span={12}>
                          <DatePickerField
                            name={`nationalTreasuryReturnBackgrounds.${idx}.fiscalYear`}
                            label={t('fiscal-year-of-national-treasury-return')}
                            control={control}
                            errors={errors}
                            picker="year"
                          />
                        </Col>

                        <Col span={12}>
                          <MonetaryInputField
                            name={`nationalTreasuryReturnBackgrounds.${idx}.amount`}
                            control={control}
                            label={t('amount-of-national-treasury-return')}
                            errors={errors}
                            unit={unit}
                          />
                        </Col>

                        <Col span={24}>
                          <TextAreaInputField
                            name={`nationalTreasuryReturnBackgrounds.${idx}.reasons`}
                            label={t('reason-of-national-treasury-return')}
                            control={control}
                            errors={errors}
                            rows={2}
                          />
                        </Col>
                      </Row>
                    </RemovableCard>
                  </Col>
                );
              })}
              <Button
                type="dashed"
                onClick={() => {
                  appendNationalTreasuryReturnBackgrounds({
                    fiscalYear: null,
                    amount: null,
                    reasons: '',
                  });
                }}
                block
                icon={<PlusOutlined />}
              >
                {t('add-national-treasury-return-background')}
              </Button>
            </Row>
          </Form.Item>
          {!isBefore2025 && (
            <>
              <Text
                variant="h5"
                text={t(
                  'effort-and-result-overview-of-reviews-carried-out-in-the-past',
                )}
                style={{ display: 'block', paddingBottom: 16 }}
              />
              <TextAreaInputField
                name="effortAndResultAndOverview"
                label=""
                control={control}
                errors={errors}
                rows={2}
              />
            </>
          )}
        </>
      )}

      {initialValues?.fundSheetType === 'standard' && (
        <Row gutter={[24, 0]}>
          <Col span={24}>
            <Text
              variant="h5"
              text={t('matter-of-act-for-normalization-of-grant')}
              style={{ display: 'block', paddingBottom: 12 }}
            />
            <TextAreaInputField
              name="matterOfActForNormalizationOfGrant"
              label=""
              control={control}
              errors={errors}
              rows={2}
            />
          </Col>
        </Row>
      )}

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
